/**
 * Map site UI language (ISO 639-1) to a typical vehicle-registration country (ISO 3166-1 alpha-2).
 * Used to default license-plate country to match the selected site language.
 */
export const LANGUAGE_TO_COUNTRY = {
    de: 'DE',
    en: 'GB',
    cs: 'CZ',
    sk: 'SK',
    pl: 'PL',
    fr: 'FR',
    it: 'IT',
    es: 'ES',
    nl: 'NL',
    hu: 'HU',
    ro: 'RO',
    bg: 'BG',
    hr: 'HR',
    sl: 'SI',
    uk: 'UA',
    el: 'GR',
    pt: 'PT',
    sv: 'SE',
    da: 'DK',
    fi: 'FI',
    no: 'NO',
    is: 'IS',
    et: 'EE',
    lv: 'LV',
    lt: 'LT',
    mk: 'MK',
    sr: 'RS',
    bs: 'BA',
    sq: 'AL',
    ar: 'SA',
    tr: 'TR',
    he: 'IL',
    zh: 'CN',
    ja: 'JP',
    ko: 'KR',
};

/** Last plate country written by language auto-sync (not by the user). */
export const AUTO_SELECTED_PLATE_COUNTRY_KEY = 'autoSelectedPlateCountry';

/** Language code for which auto-sync already ran, so navigation does not repeat it. */
export const CART_LANG_CODE_APPLIED_KEY = 'cartLangCodeApplied';

/**
 * Some APIs use "UK", others "GB" for United Kingdom — try both for English UI.
 * @param {Set<string>} codes
 * @returns {string|null}
 */
function resolveUnitedKingdomCode(codes) {
    if (codes.has('GB')) return 'GB';
    if (codes.has('UK')) return 'UK';
    return null;
}

/**
 * `ro-md` must stay Moldova. Slicing on `-` first would turn it into Romanian (`ro` → RO).
 * @param {string} normalized lowercased, trimmed language code
 * @returns {string}
 */
function normalizeLangCode(normalized) {
    if (normalized === 'ro-md' || normalized === 'moldova-ro' || normalized === 'ro_md') {
        return 'md';
    }
    return normalized.split(/[-_]/)[0];
}

/**
 * @param {string|null|undefined} langCode e.g. "de", "en-GB", "ro-md"
 * @param {{ code: string }[]} countries from API
 * @returns {string} a code present in `countries` when the list is non-empty, else "DE"
 */
export function resolveDefaultVehicleCountryCode(langCode, countries) {
    const list = Array.isArray(countries) ? countries : [];
    const codes = new Set(list.map((c) => c && c.code).filter(Boolean));

    const raw = normalizeLangCode(String(langCode || '').toLowerCase().trim());

    const mapped = LANGUAGE_TO_COUNTRY[raw];
    if (mapped) {
        if (mapped === 'GB') {
            const ukCode = resolveUnitedKingdomCode(codes);
            if (ukCode) return ukCode;
        } else if (codes.has(mapped)) {
            return mapped;
        }
    }

    if (raw.length === 2) {
        const upper = raw.toUpperCase();
        if (codes.has(upper)) return upper;
    }

    const first = list[0]?.code;
    if (first && codes.has(first)) return first;

    return codes.has('DE') ? 'DE' : (first || 'DE');
}

/**
 * Pure check: empty plate country is not an override; a filled value with no mirror
 * (or a mirror that no longer matches) is a manual / restored choice.
 * @param {string|null|undefined} plateCountry
 * @param {string|null|undefined} mirroredCountry
 * @returns {boolean}
 */
export function isManualPlateCountryOverride(plateCountry, mirroredCountry) {
    const current = String(plateCountry ?? '').trim();
    if (!current) return false;
    const mirror = String(mirroredCountry ?? '').trim();
    if (!mirror) return true;
    return mirror.toUpperCase() !== current.toUpperCase();
}

/**
 * @param {{ plate_country?: string }|null|undefined} formData
 * @returns {boolean}
 */
export function hasManualPlateCountryOverride(formData) {
    let mirror = '';
    try {
        if (typeof localStorage !== 'undefined') {
            mirror = localStorage.getItem(AUTO_SELECTED_PLATE_COUNTRY_KEY) || '';
        }
    } catch (e) { /* ignore */ }
    return isManualPlateCountryOverride(formData?.plate_country, mirror);
}

/**
 * Country passed to sorted payment methods: saved plate, then language map, then CZ.
 * @param {{ plate_country?: string }|null|undefined} formData
 * @param {string|null|undefined} langCode
 * @param {{ code: string }[]} countries
 * @returns {string}
 */
export function resolvePaymentMethodsCountry(formData, langCode, countries) {
    const plate = String(formData?.plate_country ?? '').trim().toUpperCase();
    if (plate) return plate;

    const list = Array.isArray(countries) ? countries : [];
    if (list.length) {
        const mapped = String(resolveDefaultVehicleCountryCode(langCode, list) || '').trim().toUpperCase();
        const codes = new Set(list.map((c) => c && String(c.code || '').toUpperCase()).filter(Boolean));
        if (mapped && codes.has(mapped)) return mapped;
    }

    return 'CZ';
}
